import React, { useEffect, useRef, useState, useCallback } from "react";
import "../Style/saasservices.css";

import saas1 from "../assets/icon1.png";
import saas2 from "../assets/icon2.png";
import saas3 from "../assets/icon3.png";
import saas4 from "../assets/icon4.png";
import saas5 from "../assets/icon5.png";
import saas6 from "../assets/icon6.png";

const services = [
  {
    icon: saas1,
    title: "Custom SaaS Solutions",
    desc: "We build tailor-made SaaS applications designed around your business model, workflows, and customer needs, helping you improve efficiency and accelerate digital transformation.",
    tags: [
      "Custom SaaS",
      "Business Automation",
      "Cloud Software",
      "Scalable Platform",
      "SaaS Development",
    ],
  },
  {
    icon: saas2,
    title: "Scalable Cloud Architecture",
    desc: "Our cloud-native SaaS platforms are built to handle growing users, increasing workloads, and future expansion without compromising speed or reliability.",
    tags: [
      "Cloud Architecture",
      "High Scalability",
      "Multi-Tenant",
      "SaaS Platform",
      "Performance",
    ],
  },
  {
    icon: saas3,
    title: "Enterprise-Grade Security",
    desc: "Protect your business and customer data with advanced authentication, encrypted storage, secure APIs, and compliance-focused development practices.",
    tags: [
      "Data Security",
      "Secure Login",
      "API Security",
      "Encryption",
      "Compliance",
    ],
  },
  {
    icon: saas4,
    title: "Flexible & Future-Ready Platform",
    desc: "Our SaaS products are designed for continuous growth with modular architecture, allowing easy feature additions, integrations, and performance upgrades.",
    tags: [
      "Flexible Software",
      "Future Ready",
      "Easy Upgrades",
      "Modular Design",
      "Cloud Platform",
    ],
  },
  {
    icon: saas5,
    title: "Reliable Performance & Maintenance",
    desc: "We provide continuous monitoring, bug fixes, feature enhancements, and proactive maintenance to ensure your SaaS application performs at its best.",
    tags: [
      "SaaS Support",
      "Performance",
      "Maintenance",
      "Monitoring",
      "24/7 Reliability",
    ],
  },
  {
    icon: saas6,
    title: "User-Friendly Experience",
    desc: "Our UX-focused SaaS solutions simplify complex workflows with intuitive interfaces that increase user adoption, productivity, and customer satisfaction.",
    tags: [
      "UI/UX Design",
      "Easy Navigation",
      "Better Experience",
      "User Adoption",
      "SaaS Interface",
    ],
  },
];

const AUTOPLAY_DELAY = 4500;

function ServicesSection() {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const subtextRef = useRef(null);
  const autoplayRef = useRef(null);

  const [sectionVisible, setSectionVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const total = services.length;

  // Scroll-reveal for heading/subtext
  useEffect(() => {
    const targets = [headingRef.current, subtextRef.current].filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setSectionVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Autoplay
  useEffect(() => {
    if (isPaused) return;
    autoplayRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % total);
    }, AUTOPLAY_DELAY);
    return () => clearInterval(autoplayRef.current);
  }, [isPaused, total]);

  const goTo = useCallback(
    (index) => {
      setActiveIndex(((index % total) + total) % total);
    },
    [total]
  );

  const goPrev = () => goTo(activeIndex - 1);
  const goNext = () => goTo(activeIndex + 1);

  // Position of each card relative to the active one (-1, 0, 1, or "far")
  const getPosition = (index) => {
    let diff = index - activeIndex;
    if (diff > total / 2) diff -= total;
    if (diff < -total / 2) diff += total;

    if (diff === 0) return "active";
    if (diff === -1) return "prev";
    if (diff === 1) return "next";
    return "far";
  };

  return (
    <section
      className="saas-services-section"
      ref={sectionRef}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <span className="saas-services-watermark" aria-hidden="true">
        SERVICES
      </span>

      <span
        className={`saas-services-eyebrow ${
          sectionVisible ? "saas-reveal" : ""
        }`}
      >
        What We Offer
      </span>

      <h2
        className={`saas-services-heading ${
          sectionVisible ? "saas-reveal" : ""
        }`}
        ref={headingRef}
      >
        Why Businesses Choose Custom SaaS Product Development
      </h2>

      <p
        className={`saas-services-subtext ${
          sectionVisible ? "saas-reveal" : ""
        }`}
        ref={subtextRef}
      >
        Our SaaS development services are designed to help businesses build
        scalable, subscription-based software with powerful features,
        enterprise-grade security, and seamless user experiences.
      </p>

      <div className="saas-carousel">
        <button
          type="button"
          className="saas-carousel-arrow saas-carousel-arrow-left"
          onClick={goPrev}
          aria-label="Previous service"
        >
          ‹
        </button>

        <div className="saas-carousel-track">
          {services.map((service, index) => {
            const position = getPosition(index);
            return (
              <div
                className={`saas-service-card is-${position}`}
                key={service.title}
                onClick={() => position !== "active" && goTo(index)}
              >
                <div className="saas-service-icon-wrap">
                  <img
                    src={service.icon}
                    alt={service.title}
                    className="saas-service-icon"
                  />
                </div>

                <h3>{service.title}</h3>
                <p>{service.desc}</p>

                <div className="saas-service-tags">
                  {service.tags.slice(0, 3).map((tag, i) => (
                    <span key={i}>{tag}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className="saas-carousel-arrow saas-carousel-arrow-right"
          onClick={goNext}
          aria-label="Next service"
        >
          ›
        </button>
      </div>

      <div className="saas-carousel-dots">
        {services.map((_, index) => (
          <button
            key={index}
            type="button"
            className={`saas-dot ${index === activeIndex ? "is-active" : ""}`}
            onClick={() => goTo(index)}
            aria-label={`Go to service ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
}

export default ServicesSection;